import { createModule, SagaSlice } from 'saga-slice';
import { put } from 'redux-saga/effects';
import firebase from 'firebase/app';
import 'firebase/auth';
import { Immutable } from 'immer';
import { api } from '../utils/api';
import etrApi from '../utils/etrApi';
import { api as followUpApi } from '../utils/followUpApi';
import { api as analyticsApi } from '../utils/analyticsApi';
import msTeamsApi from '../utils/msTeamsApi';
import { userDataActions } from '../userData/slice';
import {
  getSessionToken,
  setSessionToken,
  setupHeaders,
  signIn,
  signOut,
} from './utils';

type AppState = {
  signedIn: boolean;
  loading: boolean;
  popupModalOpen: boolean;
  error: Error;
};

type AppStateImmutable = Immutable<AppState>;
export type { AppStateImmutable as AppState };

export function* initialSaga(slice) {
  yield put(slice.actions.authenticate());
}

export function signOutSagaFactory() {
  return function* () {
    try {
      yield signOut();
      window.location.reload();
    } catch (error) {}
  };
}

function signInSagaFactory() {
  return function* () {
    try {
      yield signIn();
    } catch (e) {}
  };
}

export function authenticateSagaFactory(actions) {
  return function* () {
    try {
      const sessionToken = getSessionToken();
      if (sessionToken) {
        setupHeaders([api, etrApi, followUpApi, analyticsApi], sessionToken);
        yield put(userDataActions.fetchUserData());
        yield put(actions.authenticateSuccess());
        return;
      } else {
        const provider = new firebase.auth.OAuthProvider('microsoft.com');
        provider.setCustomParameters({
          tenant: process.env.REACT_APP_AZURE_CREDS_TENANT,
          prompt: 'select_account',
        });
        const popupResult = yield firebase.auth().signInWithPopup(provider);
        const { data } = yield etrApi.post('ppm/auth/login', {
          idToken: popupResult.user.Aa,
          userDetails: popupResult?.additionalUserInfo?.profile,
        });
        const { customAccessToken } = data;

        setSessionToken(customAccessToken);
        setupHeaders(
          [api, etrApi, followUpApi, analyticsApi, msTeamsApi],
          customAccessToken,
        );

        yield put(userDataActions.fetchUserData());
        yield put(actions.authenticateSuccess());
        return;
      }
    } catch (e) {
      if (e.code === 'auth/popup-blocked') {
        yield put(actions.openPopupModal());
      } else {
        yield put(actions.signOut());
      }
    }
  };
}

const slice: SagaSlice = createModule({
  name: 'app',
  initialState: {
    loading: false,
  },
  reducers: {
    authenticate: (state: AppState) => {
      state.signedIn = false;
      state.loading = true;
      state.error = null;
    },
    authenticateFail: (state: AppState, error: Error) => {
      state.signedIn = false;
      state.loading = false;
      state.error = error;
    },
    authenticateSuccess: (state: AppState) => {
      state.signedIn = true;
      state.error = null;
    },
    signIn: (state: AppState) => {
      state.loading = true;
    },
    signOut: (state: AppState) => {
      state.signedIn = false;
      state.loading = true;
      state.error = null;
    },
    signOutSuccess: (state: AppState) => {
      state.signedIn = false;
      state.loading = false;
      state.error = null;
    },
    openPopupModal: (state: AppState) => {
      state.popupModalOpen = true;
    },
  },

  sagas: (actions) => ({
    [actions.authenticate]: authenticateSagaFactory(actions),
    [actions.signOut]: signOutSagaFactory(),
    [actions.signIn]: signInSagaFactory(),
  }),
});

export default slice;
export const appActions = slice.actions;
